export const documents = {
  extends: 'Page',
  flow: 'x',
  minHeight: '100vh',
  background: 'surface',
  state: {
    docTab: 'create',
    ocrModalOpen: false,
    ocrStep: 'upload'
  },

  Sidebar: {},

  MainContent: {
    flow: 'y',
    flex: '1',
    padding: 'B C',
    gap: 'B',
    overflow: 'auto',
    '@tabletS': { padding: 'A B' },
    '@mobileL': { padding: 'A' },

    PageHeader: {
      flow: 'x',
      align: 'center space-between',
      '@mobileL': { flow: 'y', gap: 'A' },
      HeaderTitle: {
        tag: 'h1',
        text: '{{ docsTitle | polyglot }}',
        fontSize: 'C',
        fontWeight: '800',
        color: 'text',
        '@mobileL': { fontSize: 'B' }
      },
      ThemeToggle: {}
    },

    // ─── Tabs ─────────────────────────────────────
    DocTabs: {
      flow: 'x',
      gap: 'Z',
      borderBottom: '1px solid surfaceBorderStrong',
      padding: '0 0 Z 0',
      '@mobileL': { flexWrap: 'wrap' },
      TabCreate: {
        extends: 'Button',
        text: '{{ docsCreate | polyglot }}',
        padding: 'Z A',
        fontSize: 'Z',
        cursor: 'pointer',
        borderRadius: 'Z',
        background: (el, s) =>
          s.docTab === 'create' ? 'green' : 'transparent',
        color: (el, s) => (s.docTab === 'create' ? 'black' : 'textSoft'),
        fontWeight: (el, s) => (s.docTab === 'create' ? '600' : '400'),
        onClick: (e, el, s) => s.update({ docTab: 'create' })
      },
      TabExisting: {
        extends: 'Button',
        text: '{{ docsExisting | polyglot }}',
        padding: 'Z A',
        fontSize: 'Z',
        cursor: 'pointer',
        borderRadius: 'Z',
        background: (el, s) =>
          s.docTab === 'existing' ? 'green' : 'transparent',
        color: (el, s) => (s.docTab === 'existing' ? 'black' : 'textSoft'),
        fontWeight: (el, s) => (s.docTab === 'existing' ? '600' : '400'),
        onClick: (e, el, s) => s.update({ docTab: 'existing' })
      },
      TabFiles: {
        extends: 'Button',
        text: '{{ docsFiles | polyglot }}',
        padding: 'Z A',
        fontSize: 'Z',
        cursor: 'pointer',
        borderRadius: 'Z',
        background: (el, s) => (s.docTab === 'files' ? 'green' : 'transparent'),
        color: (el, s) => (s.docTab === 'files' ? 'black' : 'textSoft'),
        fontWeight: (el, s) => (s.docTab === 'files' ? '600' : '400'),
        onClick: (e, el, s) => s.update({ docTab: 'files' })
      }
    },

    // OCR integration banner
    OcrBanner: {
      flow: 'x',
      gap: 'Z',
      align: 'center',
      padding: 'Z A',
      background: 'greenBg',
      border: '1px solid greenBorder',
      borderRadius: 'Z',
      BannerDot: {
        width: 'Y',
        height: 'Y',
        borderRadius: '100%',
        background: 'green',
        flexShrink: '0'
      },
      BannerText: {
        text: '{{ docsOcrNote | polyglot }}',
        fontSize: 'Y',
        color: 'green',
        flex: '1',
        lineHeight: '1.4'
      },
      OcrBtn: {
        extends: 'Button',
        text: '{{ docsOcrScan | polyglot }}',
        background: 'transparent',
        color: 'green',
        border: '1px solid greenBorder',
        padding: 'Y Z',
        fontSize: 'Y',
        borderRadius: 'Z',
        cursor: 'pointer',
        onClick: (e, el, s) =>
          s.update({ ocrModalOpen: true, ocrStep: 'upload' })
      }
    },

    // ─── OCR Modal ──────────────────────────────
    OcrModalOverlay: {
      if: (el, s) => s.ocrModalOpen,
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      zIndex: '1000',
      background: 'black.7',
      flow: 'x',
      align: 'center center',
      onClick: (e, el, s) => {
        if (e.target === el.node) s.update({ ocrModalOpen: false })
      },

      OcrModal: {
        flow: 'y',
        gap: 'A',
        padding: 'C',
        background: 'surfaceMid',
        border: '1px solid surfaceBorderStrong',
        borderRadius: 'Z',
        width: '100%',
        maxWidth: 'I',
        height: '85vh',
        maxHeight: '85vh',
        overflow: 'auto',

        // Modal header
        OcrHeader: {
          flow: 'x',
          align: 'center space-between',
          OcrTitle: {
            tag: 'h2',
            text: '{{ docsOcrTitle | polyglot }}',
            fontSize: 'B',
            fontWeight: '700',
            color: 'text'
          },
          OcrCloseBtn: {
            extends: 'Button',
            text: '✕',
            theme: 'transparent',
            background: 'transparent',
            border: 'none',
            color: 'textSoft',
            fontSize: 'A',
            padding: 'Z',
            cursor: 'pointer',
            onClick: (e, el, s) => s.update({ ocrModalOpen: false })
          }
        },

        // Step 1 — Upload
        OcrUploadStep: {
          flow: 'y',
          gap: 'A',
          align: 'center center',
          show: (el, s) => s.ocrStep === 'upload',

          UploadZone: {
            flow: 'y',
            gap: 'Z',
            align: 'center center',
            padding: 'D B',
            border: '2px dashed surfaceBorderStrong',
            borderRadius: 'Z',
            width: '100%',
            cursor: 'pointer',
            transition: 'border-color 200ms',
            ':hover': { borderColor: 'green' },
            onClick: (e, el, s) => {
              s.update({ ocrStep: 'scanning' })
              setTimeout(() => s.update({ ocrStep: 'result' }), 2500)
            },
            UploadIcon: { text: '📄', fontSize: 'D' },
            UploadText: {
              text: '{{ docsUploadText | polyglot }}',
              fontSize: 'A',
              fontWeight: '600',
              color: 'text',
              textAlign: 'center'
            },
            UploadSub: {
              text: '{{ docsUploadSub | polyglot }}',
              fontSize: 'Z',
              color: 'textMuted',
              textAlign: 'center'
            },
            UploadFormats: {
              text: '{{ docsUploadFormats | polyglot }}',
              fontSize: 'Y',
              color: 'textMuted'
            }
          }
        },

        // Step 2 — Scanning animation
        OcrScanningStep: {
          flow: 'y',
          gap: 'A',
          align: 'center center',
          padding: 'C',
          show: (el, s) => s.ocrStep === 'scanning',

          ScanIcon: { text: '🔍', fontSize: 'D' },
          ScanText: {
            text: '{{ docsScanning | polyglot }}',
            fontSize: 'A',
            fontWeight: '600',
            color: 'green'
          },
          ScanProgress: {
            width: '100%',
            maxWidth: 'F',
            height: 'Y',
            background: 'surfaceBorder',
            borderRadius: 'Z',
            ScanFill: {
              width: '70%',
              height: '100%',
              background: 'green',
              borderRadius: 'Z',
              transition: 'width 2s'
            }
          },
          ScanDetails: {
            flow: 'y',
            gap: 'Y',
            align: 'center',
            ScanDetail1: {
              text: '✓ {{ docsTextRecognition | polyglot }}',
              fontSize: 'Z',
              color: 'green'
            },
            ScanDetail2: {
              text: '✓ {{ docsGeorgianSymbols | polyglot }}',
              fontSize: 'Z',
              color: 'green'
            },
            ScanDetail3: {
              text: '⟳ {{ docsFormatAnalysis | polyglot }}',
              fontSize: 'Z',
              color: 'textMuted'
            }
          }
        },

        // Step 3 — Result
        OcrResultStep: {
          flow: 'y',
          gap: 'A',
          flex: '1',
          show: (el, s) => s.ocrStep === 'result',

          ResultBadge: {
            flow: 'x',
            gap: 'Z',
            align: 'center',
            alignSelf: 'flex-start',
            padding: 'Y Z',
            background: 'greenBg',
            border: '1px solid greenBorder',
            borderRadius: 'Z',
            BadgeDot: {
              width: 'Y',
              height: 'Y',
              borderRadius: '100%',
              background: 'green'
            },
            BadgeText: {
              text: '{{ docsOcrSuccess | polyglot }}',
              fontSize: 'Y',
              color: 'green',
              fontWeight: '600'
            }
          },

          ResultMeta: {
            flow: 'x',
            gap: 'A',
            flexWrap: 'wrap',
            MetaItem1: {
              flow: 'y',
              gap: 'Y',
              MetaLabel: {
                text: '{{ docsFile | polyglot }}',
                fontSize: 'Y',
                color: 'textMuted'
              },
              MetaVal: {
                text: '{{ docsContractScanPdf | polyglot }}',
                fontSize: 'Z',
                color: 'text',
                fontWeight: '500'
              }
            },
            MetaItem2: {
              flow: 'y',
              gap: 'Y',
              MetaLabel: {
                text: '{{ docsPages | polyglot }}',
                fontSize: 'Y',
                color: 'textMuted'
              },
              MetaVal: {
                text: '3',
                fontSize: 'Z',
                color: 'text',
                fontWeight: '500'
              }
            },
            MetaItem3: {
              flow: 'y',
              gap: 'Y',
              MetaLabel: {
                text: '{{ docsLanguage | polyglot }}',
                fontSize: 'Y',
                color: 'textMuted'
              },
              MetaVal: {
                text: '{{ docsGeorgian | polyglot }}',
                fontSize: 'Z',
                color: 'text',
                fontWeight: '500'
              }
            }
          },

          ResultText: {
            padding: 'A',
            background: 'surfaceCard',
            borderRadius: 'Z',
            border: '1px solid surfaceBorder',
            flex: '1',
            minHeight: 'G',
            overflow: 'auto',
            text: '{{ docsOcrResultText | polyglot }}',
            fontSize: 'Z',
            color: 'textSoft',
            lineHeight: '1.8',
            whiteSpace: 'pre-wrap'
          },

          ResultActions: {
            flow: 'x',
            gap: 'Z',
            flexWrap: 'wrap',
            SaveDocBtn: {
              extends: 'Button',
              text: '{{ docsAddToCase | polyglot }}',
              background: 'green',
              color: 'neutral950',
              fontWeight: '600',
              padding: 'Z A',
              fontSize: 'Z',
              borderRadius: 'Z',
              border: 'none',
              cursor: 'pointer',
              onClick: (e, el, s) => s.update({ ocrModalOpen: false })
            },
            CopyOcrBtn: {
              extends: 'Button',
              text: '{{ docsCopy | polyglot }}',
              background: 'transparent',
              color: 'neutral300',
              border: '1px solid neutral700',
              padding: 'Z A',
              fontSize: 'Z',
              borderRadius: 'Z',
              cursor: 'pointer'
            },
            RescanBtn: {
              extends: 'Button',
              text: '{{ docsRescan | polyglot }}',
              background: 'transparent',
              color: 'neutral300',
              border: '1px solid neutral700',
              padding: 'Z A',
              fontSize: 'Z',
              borderRadius: 'Z',
              cursor: 'pointer',
              onClick: (e, el, s) => {
                s.update({ ocrStep: 'scanning' })
                setTimeout(() => s.update({ ocrStep: 'result' }), 2500)
              }
            }
          }
        }
      }
    },

    // ═══════════════════════════════════════════════
    // TAB 1 — Create document (AI drafting)
    // ═══════════════════════════════════════════════
    CreateView: {
      flow: 'x',
      gap: 'A',
      flex: '1',
      show: (el, s) => s.docTab === 'create',
      '@tabletS': { flow: 'y' },

      TemplateList: {
        flow: 'y',
        gap: 'Z',
        width: 'G',
        flexShrink: '0',

        TplDemand: {
          padding: 'Z A',
          borderRadius: 'Z',
          cursor: 'pointer',
          fontSize: 'A',
          fontWeight: (el, s) =>
            s.root.selectedTemplate === 'demand' ? '600' : '400',
          background: (el, s) =>
            s.root.selectedTemplate === 'demand' ? 'greenBg' : 'transparent',
          color: (el, s) =>
            s.root.selectedTemplate === 'demand' ? 'green' : 'textSoft',
          border: 'none',
          text: '{{ docsClaim | polyglot }}',
          onClick: (e, el, s) =>
            s.root.update({ selectedTemplate: 'demand', docOutput: null })
        },
        TplPower: {
          padding: 'Z A',
          borderRadius: 'Z',
          cursor: 'pointer',
          fontSize: 'A',
          fontWeight: (el, s) =>
            s.root.selectedTemplate === 'power' ? '600' : '400',
          background: (el, s) =>
            s.root.selectedTemplate === 'power' ? 'greenBg' : 'transparent',
          color: (el, s) =>
            s.root.selectedTemplate === 'power' ? 'green' : 'textSoft',
          border: 'none',
          text: '{{ docsPowerOfAttorney | polyglot }}',
          onClick: (e, el, s) =>
            s.root.update({ selectedTemplate: 'power', docOutput: null })
        },
        TplTermination: {
          padding: 'Z A',
          borderRadius: 'Z',
          cursor: 'pointer',
          fontSize: 'A',
          fontWeight: (el, s) =>
            s.root.selectedTemplate === 'termination' ? '600' : '400',
          background: (el, s) =>
            s.root.selectedTemplate === 'termination'
              ? 'greenBg'
              : 'transparent',
          color: (el, s) =>
            s.root.selectedTemplate === 'termination' ? 'green' : 'textSoft',
          border: 'none',
          text: '{{ docsNotification | polyglot }}',
          onClick: (e, el, s) =>
            s.root.update({ selectedTemplate: 'termination', docOutput: null })
        },
        TplContract: {
          padding: 'Z A',
          borderRadius: 'Z',
          cursor: 'pointer',
          fontSize: 'A',
          fontWeight: (el, s) =>
            s.root.selectedTemplate === 'contract' ? '600' : '400',
          background: (el, s) =>
            s.root.selectedTemplate === 'contract' ? 'greenBg' : 'transparent',
          color: (el, s) =>
            s.root.selectedTemplate === 'contract' ? 'green' : 'textSoft',
          border: 'none',
          text: '{{ docsContractReview | polyglot }}',
          onClick: (e, el, s) =>
            s.root.update({ selectedTemplate: 'contract', docOutput: null })
        },
        TplFree: {
          padding: 'Z A',
          borderRadius: 'Z',
          cursor: 'pointer',
          fontSize: 'A',
          fontWeight: (el, s) =>
            s.root.selectedTemplate === 'free' ? '600' : '400',
          background: (el, s) =>
            s.root.selectedTemplate === 'free' ? 'greenBg' : 'transparent',
          color: (el, s) =>
            s.root.selectedTemplate === 'free' ? 'green' : 'textSoft',
          border: 'none',
          text: '{{ docsFreeForm | polyglot }}',
          onClick: (e, el, s) =>
            s.root.update({ selectedTemplate: 'free', docOutput: null })
        }
      },

      DocForm: {
        margin: '0',
        tag: 'form',
        flow: 'y',
        gap: 'A',
        flex: '1',
        maxWidth: 'G',
        padding: 'A',
        background: 'surfaceMid',
        border: '1px solid surfaceBorderStrong',
        borderRadius: 'Z',
        FormTitle: {
          tag: 'h2',
          fontSize: 'B',
          fontWeight: '700',
          color: 'text',
          text: (el, s) => {
            const n = {
              demand: '{{ docsClaim | polyglot }}',
              power: '{{ docsPowerOfAttorney | polyglot }}',
              termination: '{{ docsNotification | polyglot }}',
              contract: '{{ docsContractReview | polyglot }}',
              free: '{{ docsFreeForm | polyglot }}'
            }
            return n[s.root.selectedTemplate] || '{{ docsDocument | polyglot }}'
          }
        },
        Party1Label: {
          tag: 'label',
          text: '{{ docsParty1 | polyglot }}',
          fontSize: 'Z',
          color: 'textSoft',
          fontWeight: '500'
        },
        Party1Input: {
          tag: 'input',
          type: 'text',
          name: 'party1',
          placeholder: '{{ docsNameCompany | polyglot }}',
          padding: 'Z A',
          fontSize: 'A',
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          borderRadius: 'Z',
          color: 'text'
        },
        Party2Label: {
          tag: 'label',
          text: '{{ docsParty2 | polyglot }}',
          fontSize: 'Z',
          color: 'textSoft',
          fontWeight: '500'
        },
        Party2Input: {
          tag: 'input',
          type: 'text',
          name: 'party2',
          placeholder: '{{ docsNameCompany | polyglot }}',
          padding: 'Z A',
          fontSize: 'A',
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          borderRadius: 'Z',
          color: 'text'
        },
        DateLabel: {
          tag: 'label',
          text: '{{ docsDate | polyglot }}',
          fontSize: 'Z',
          color: 'textSoft',
          fontWeight: '500'
        },
        DateInput: {
          tag: 'input',
          type: 'date',
          name: 'date',
          padding: 'Z A',
          fontSize: 'A',
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          borderRadius: 'Z',
          color: 'text'
        },
        NotesLabel: {
          tag: 'label',
          text: '{{ docsAdditionalInfo | polyglot }}',
          fontSize: 'Z',
          color: 'textSoft',
          fontWeight: '500'
        },
        NotesInput: {
          tag: 'textarea',
          name: 'notes',
          placeholder: '{{ docsFreeText | polyglot }}',
          padding: 'Z A',
          fontSize: 'A',
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          borderRadius: 'Z',
          color: 'text',
          minHeight: 'D'
        },
        GenerateBtn: {
          extends: 'Button',
          text: (el, s) =>
            s.root.generatingDoc
              ? '{{ docsGenerating | polyglot }}'
              : '{{ docsGenerate | polyglot }}',
          background: 'green',
          color: 'neutral950',
          border: 'none',
          padding: 'Z A',
          fontSize: 'A',
          fontWeight: '700',
          borderRadius: 'Z',
          width: '100%',
          textAlign: 'center',
          cursor: 'pointer',
          opacity: (el, s) => (s.root.generatingDoc ? '.6' : '1'),
          onClick: (e, el, s) => {
            e.preventDefault()
            s.root.update({ generatingDoc: true })
            setTimeout(() => {
              s.root.update({
                generatingDoc: false,
                docOutput: '{{ docsGeneratedClaimText | polyglot }}'
              })
            }, 2000)
          }
        }
      },

      DocOutput: {
        flow: 'y',
        gap: 'A',
        flex: '1.5',
        padding: 'A',
        background: 'surfaceMid',
        border: '1px solid surfaceBorderStrong',
        borderRadius: 'Z',
        minHeight: 'G',
        '@tabletS': { maxWidth: '100%', width: '100%' },
        OutputTitle: {
          tag: 'h2',
          text: '{{ docsGeneratedDocument | polyglot }}',
          fontSize: 'B',
          fontWeight: '700',
          color: 'text'
        },
        LoadingState: {
          flow: 'y',
          align: 'center center',
          flex: '1',
          show: (el, s) => s.root.generatingDoc,
          Spinner: { text: '⟳', fontSize: 'D', color: 'green' },
          LoadingText: {
            text: '{{ docsGenerating | polyglot }}',
            fontSize: 'A',
            color: 'textSoft'
          }
        },
        EmptyState: {
          flow: 'y',
          align: 'center center',
          flex: '1',
          gap: 'A',
          show: (el, s) => !s.root.generatingDoc && !s.root.docOutput,
          EmptyIcon: {
            extends: 'Icon',
            icon: 'document',
            color: 'surfaceBorderStrong',
            width: 'C',
            height: 'C'
          },
          EmptyText: {
            text: '{{ docsEmptyState | polyglot }}',
            fontSize: 'A',
            color: 'textMuted',
            textAlign: 'center'
          },
          DemoBtn: {
            extends: 'Button',
            text: '{{ docsShowDemo | polyglot }}',
            background: 'transparent',
            color: 'green',
            border: '1px solid greenBorder',
            padding: 'Z A',
            fontSize: 'Z',
            borderRadius: 'Z',
            cursor: 'pointer',
            onClick: (e, el, s) =>
              s.root.update({
                docOutput: '{{ docsDemoClaimText | polyglot }}'
              })
          }
        },
        OutputContent: {
          if: (el, s) => s.root.docOutput,
          flow: 'y',
          gap: 'A',
          flex: '1',
          DocText: {
            text: (el, s) => s.root.docOutput || '',
            fontSize: 'A',
            color: 'textSoft',
            lineHeight: '1.8',
            whiteSpace: 'pre-wrap',
            padding: 'A',
            background: 'surfaceCard',
            borderRadius: 'Z'
          },
          OutputActions: {
            flow: 'x',
            gap: 'Z',
            CopyBtn: {
              extends: 'Button',
              flow: 'x',
              gap: 'Z',
              align: 'center',
              background: 'transparent',
              color: 'neutral300',
              border: '1px solid neutral700',
              padding: 'Z A',
              fontSize: 'Z',
              borderRadius: 'Z',
              cursor: 'pointer',
              BtnIcon: {
                extends: 'Icon',
                icon: 'copy',
                width: 'Z',
                height: 'Z'
              },
              BtnText: { text: '{{ docsCopy | polyglot }}' }
            },
            DownloadBtn: {
              extends: 'Button',
              flow: 'x',
              gap: 'Z',
              align: 'center',
              background: 'transparent',
              color: 'neutral300',
              border: '1px solid neutral700',
              padding: 'Z A',
              fontSize: 'Z',
              borderRadius: 'Z',
              cursor: 'pointer',
              BtnIcon: {
                extends: 'Icon',
                icon: 'download',
                width: 'Z',
                height: 'Z'
              },
              BtnText: { text: '{{ docsDownload | polyglot }}' }
            },
            SendBtn: {
              extends: 'Button',
              flow: 'x',
              gap: 'Z',
              align: 'center',
              background: 'green',
              color: 'neutral950',
              border: 'none',
              fontWeight: '600',
              padding: 'Z A',
              fontSize: 'Z',
              borderRadius: 'Z',
              cursor: 'pointer',
              BtnIcon: {
                extends: 'Icon',
                icon: 'mail',
                width: 'Z',
                height: 'Z'
              },
              BtnText: { text: '{{ docsSend | polyglot }}' }
            }
          }
        }
      }
    },

    // ═══════════════════════════════════════════════
    // TAB 2 — Existing documents
    // ═══════════════════════════════════════════════
    ExistingView: {
      flow: 'y',
      gap: 'A',
      show: (el, s) => s.docTab === 'existing',

      ExistingHeader: {
        flow: 'x',
        align: 'center space-between',
        ExistingTitle: {
          tag: 'h2',
          text: '{{ docsCreatedDocuments | polyglot }}',
          fontSize: 'B',
          fontWeight: '700',
          color: 'text'
        },
        SearchDocs: {
          tag: 'input',
          type: 'text',
          name: 'searchDocs',
          placeholder: '{{ docsSearch | polyglot }}',
          padding: 'Z A',
          fontSize: 'Z',
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          borderRadius: 'Z',
          color: 'text',
          width: 'F'
        }
      },

      DocRow1: {
        flow: 'x',
        align: 'center space-between',
        padding: 'A',
        background: 'surfaceCard',
        border: '1px solid surfaceBorderStrong',
        borderRadius: 'Z',
        DocLeft: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          DocFileIcon: {
            extends: 'Icon',
            icon: 'document',
            width: 'A',
            height: 'A',
            color: 'red'
          },
          DocInfo: {
            flow: 'y',
            gap: 'Y',
            DocName: {
              text: '{{ docsRow1Name | polyglot }}',
              fontSize: 'A',
              fontWeight: '500',
              color: 'text'
            },
            DocMeta: {
              text: '{{ docsRow1Meta | polyglot }}',
              fontSize: 'Z',
              color: 'textMuted'
            }
          }
        },
        DocActions: {
          flow: 'x',
          gap: 'Z',
          DlBtn: {
            extends: 'Button',
            flow: 'x',
            gap: 'Y',
            align: 'center',
            background: 'transparent',
            color: 'neutral300',
            border: '1px solid neutral700',
            padding: 'Y Z',
            fontSize: 'Z',
            borderRadius: 'Z',
            cursor: 'pointer',
            BtnIcon: {
              extends: 'Icon',
              icon: 'download',
              width: 'Z',
              height: 'Z'
            },
            BtnText: { text: '{{ docsDownload | polyglot }}' }
          },
          ShareBtn: {
            extends: 'Button',
            flow: 'x',
            gap: 'Y',
            align: 'center',
            background: 'transparent',
            color: 'neutral300',
            border: '1px solid neutral700',
            padding: 'Y Z',
            fontSize: 'Z',
            borderRadius: 'Z',
            cursor: 'pointer',
            BtnIcon: { extends: 'Icon', icon: 'mail', width: 'Z', height: 'Z' },
            BtnText: { text: '{{ docsSend | polyglot }}' }
          }
        }
      },
      DocRow2: {
        flow: 'x',
        align: 'center space-between',
        padding: 'A',
        background: 'surfaceCard',
        border: '1px solid surfaceBorderStrong',
        borderRadius: 'Z',
        DocLeft: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          DocFileIcon: {
            extends: 'Icon',
            icon: 'document',
            width: 'A',
            height: 'A',
            color: 'blue'
          },
          DocInfo: {
            flow: 'y',
            gap: 'Y',
            DocName: {
              text: '{{ docsRow2Name | polyglot }}',
              fontSize: 'A',
              fontWeight: '500',
              color: 'text'
            },
            DocMeta: {
              text: '{{ docsRow2Meta | polyglot }}',
              fontSize: 'Z',
              color: 'textMuted'
            }
          }
        },
        DocActions: {
          flow: 'x',
          gap: 'Z',
          DlBtn: {
            extends: 'Button',
            flow: 'x',
            gap: 'Y',
            align: 'center',
            background: 'transparent',
            color: 'neutral300',
            border: '1px solid neutral700',
            padding: 'Y Z',
            fontSize: 'Z',
            borderRadius: 'Z',
            cursor: 'pointer',
            BtnIcon: {
              extends: 'Icon',
              icon: 'download',
              width: 'Z',
              height: 'Z'
            },
            BtnText: { text: '{{ docsDownload | polyglot }}' }
          },
          ShareBtn: {
            extends: 'Button',
            flow: 'x',
            gap: 'Y',
            align: 'center',
            background: 'transparent',
            color: 'neutral300',
            border: '1px solid neutral700',
            padding: 'Y Z',
            fontSize: 'Z',
            borderRadius: 'Z',
            cursor: 'pointer',
            BtnIcon: { extends: 'Icon', icon: 'mail', width: 'Z', height: 'Z' },
            BtnText: { text: '{{ docsSend | polyglot }}' }
          }
        }
      },
      DocRow3: {
        flow: 'x',
        align: 'center space-between',
        padding: 'A',
        background: 'surfaceCard',
        border: '1px solid surfaceBorderStrong',
        borderRadius: 'Z',
        DocLeft: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          DocFileIcon: {
            extends: 'Icon',
            icon: 'document',
            width: 'A',
            height: 'A',
            color: 'purple'
          },
          DocInfo: {
            flow: 'y',
            gap: 'Y',
            DocName: {
              text: '{{ docsRow3Name | polyglot }}',
              fontSize: 'A',
              fontWeight: '500',
              color: 'text'
            },
            DocMeta: {
              text: '{{ docsRow3Meta | polyglot }}',
              fontSize: 'Z',
              color: 'textMuted'
            }
          }
        },
        DocActions: {
          flow: 'x',
          gap: 'Z',
          DlBtn: {
            extends: 'Button',
            flow: 'x',
            gap: 'Y',
            align: 'center',
            background: 'transparent',
            color: 'neutral300',
            border: '1px solid neutral700',
            padding: 'Y Z',
            fontSize: 'Z',
            borderRadius: 'Z',
            cursor: 'pointer',
            BtnIcon: {
              extends: 'Icon',
              icon: 'download',
              width: 'Z',
              height: 'Z'
            },
            BtnText: { text: '{{ docsDownload | polyglot }}' }
          },
          ShareBtn: {
            extends: 'Button',
            flow: 'x',
            gap: 'Y',
            align: 'center',
            background: 'transparent',
            color: 'neutral300',
            border: '1px solid neutral700',
            padding: 'Y Z',
            fontSize: 'Z',
            borderRadius: 'Z',
            cursor: 'pointer',
            BtnIcon: { extends: 'Icon', icon: 'mail', width: 'Z', height: 'Z' },
            BtnText: { text: '{{ docsSend | polyglot }}' }
          }
        }
      }
    },

    // ═══════════════════════════════════════════════
    // TAB 3 — Files (folder structure)
    // ═══════════════════════════════════════════════
    FilesView: {
      flow: 'y',
      gap: 'A',
      show: (el, s) => s.docTab === 'files',

      FilesHeader: {
        flow: 'x',
        align: 'center space-between',
        FilesTitle: {
          tag: 'h2',
          text: '{{ docsFileManager | polyglot }}',
          fontSize: 'B',
          fontWeight: '700',
          color: 'text'
        },
        UploadBtn: {
          extends: 'Button',
          text: '+ {{ docsUpload | polyglot }}',
          background: 'green',
          color: 'neutral950',
          border: 'none',
          fontWeight: '600',
          padding: 'Z A',
          fontSize: 'Z',
          borderRadius: 'Z',
          cursor: 'pointer'
        }
      },

      // Path breadcrumb
      PathBreadcrumb: {
        flow: 'x',
        gap: 'Y',
        align: 'center',
        padding: 'Z 0',
        PathRoot: {
          text: '{{ docsFiles | polyglot }}',
          fontSize: 'Z',
          color: 'green',
          cursor: 'pointer',
          fontWeight: '500'
        },
        PathSep1: { text: '/', fontSize: 'Z', color: 'textMuted' },
        PathCurrent: {
          text: '{{ docsCases | polyglot }}',
          fontSize: 'Z',
          color: 'textSoft'
        }
      },

      // Folder grid
      FoldersRow: {
        flow: 'x',
        gap: 'Z',
        flexWrap: 'wrap',

        Folder1: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          padding: 'Z A',
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          borderRadius: 'Z',
          cursor: 'pointer',
          minWidth: 'E',
          FolderIcon: { text: '📁', fontSize: 'A' },
          FolderInfo: {
            flow: 'y',
            FolderName: {
              text: '{{ docsFolder1Name | polyglot }}',
              fontSize: 'Z',
              fontWeight: '500',
              color: 'text'
            },
            FolderCount: {
              text: '{{ docsFolder1Count | polyglot }}',
              fontSize: 'Y',
              color: 'textMuted'
            }
          }
        },
        Folder2: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          padding: 'Z A',
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          borderRadius: 'Z',
          cursor: 'pointer',
          minWidth: 'E',
          FolderIcon: { text: '📁', fontSize: 'A' },
          FolderInfo: {
            flow: 'y',
            FolderName: {
              text: '{{ docsFolder2Name | polyglot }}',
              fontSize: 'Z',
              fontWeight: '500',
              color: 'text'
            },
            FolderCount: {
              text: '{{ docsFolder2Count | polyglot }}',
              fontSize: 'Y',
              color: 'textMuted'
            }
          }
        },
        Folder3: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          padding: 'Z A',
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          borderRadius: 'Z',
          cursor: 'pointer',
          minWidth: 'E',
          FolderIcon: { text: '📁', fontSize: 'A' },
          FolderInfo: {
            flow: 'y',
            FolderName: {
              text: '{{ docsFolder3Name | polyglot }}',
              fontSize: 'Z',
              fontWeight: '500',
              color: 'text'
            },
            FolderCount: {
              text: '{{ docsFolder3Count | polyglot }}',
              fontSize: 'Y',
              color: 'textMuted'
            }
          }
        },
        FolderTemplates: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          padding: 'Z A',
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          borderRadius: 'Z',
          cursor: 'pointer',
          minWidth: 'E',
          FolderIcon: { text: '📁', fontSize: 'A' },
          FolderInfo: {
            flow: 'y',
            FolderName: {
              text: '{{ docsTemplates | polyglot }}',
              fontSize: 'Z',
              fontWeight: '500',
              color: 'text'
            },
            FolderCount: {
              text: '{{ docsFolder4Count | polyglot }}',
              fontSize: 'Y',
              color: 'textMuted'
            }
          }
        }
      },

      // Files list
      FilesSectionTitle: {
        text: '{{ docsRecentFiles | polyglot }}',
        fontSize: 'A',
        fontWeight: '600',
        color: 'text',
        padding: 'Z 0 0 0'
      },

      FileRow1: {
        flow: 'x',
        align: 'center space-between',
        padding: 'Z A',
        background: 'surfaceCard',
        border: '1px solid surfaceBorder',
        borderRadius: 'Z',
        FileLeft: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          FileIcon: { text: '📄', fontSize: 'A' },
          FileInfo: {
            flow: 'y',
            FileName: {
              text: '{{ docsFile1Name | polyglot }}',
              fontSize: 'Z',
              fontWeight: '500',
              color: 'text'
            },
            FileMeta: {
              text: '{{ docsFile1Meta | polyglot }}',
              fontSize: 'Y',
              color: 'textMuted'
            }
          }
        },
        FileRight: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          FileDlBtn: {
            extends: 'Icon',
            icon: 'download',
            width: 'A',
            height: 'A',
            color: 'textMuted',
            cursor: 'pointer'
          }
        }
      },
      FileRow2: {
        flow: 'x',
        align: 'center space-between',
        padding: 'Z A',
        background: 'surfaceCard',
        border: '1px solid surfaceBorder',
        borderRadius: 'Z',
        FileLeft: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          FileIcon: { text: '📄', fontSize: 'A' },
          FileInfo: {
            flow: 'y',
            FileName: {
              text: '{{ docsFile2Name | polyglot }}',
              fontSize: 'Z',
              fontWeight: '500',
              color: 'text'
            },
            FileMeta: {
              text: '{{ docsFile2Meta | polyglot }}',
              fontSize: 'Y',
              color: 'textMuted'
            }
          }
        },
        FileRight: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          FileDlBtn: {
            extends: 'Icon',
            icon: 'download',
            width: 'A',
            height: 'A',
            color: 'textMuted',
            cursor: 'pointer'
          }
        }
      },
      FileRow3: {
        flow: 'x',
        align: 'center space-between',
        padding: 'Z A',
        background: 'surfaceCard',
        border: '1px solid surfaceBorder',
        borderRadius: 'Z',
        FileLeft: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          FileIcon: { text: '📋', fontSize: 'A' },
          FileInfo: {
            flow: 'y',
            FileName: {
              text: '{{ docsFile3Name | polyglot }}',
              fontSize: 'Z',
              fontWeight: '500',
              color: 'text'
            },
            FileMeta: {
              text: '{{ docsFile3Meta | polyglot }}',
              fontSize: 'Y',
              color: 'textMuted'
            }
          }
        },
        FileRight: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          FileDlBtn: {
            extends: 'Icon',
            icon: 'download',
            width: 'A',
            height: 'A',
            color: 'textMuted',
            cursor: 'pointer'
          }
        }
      },
      FileRow4: {
        flow: 'x',
        align: 'center space-between',
        padding: 'Z A',
        background: 'surfaceCard',
        border: '1px solid surfaceBorder',
        borderRadius: 'Z',
        FileLeft: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          FileIcon: { text: '🖼', fontSize: 'A' },
          FileInfo: {
            flow: 'y',
            FileName: {
              text: '{{ docsFile4Name | polyglot }}',
              fontSize: 'Z',
              fontWeight: '500',
              color: 'text'
            },
            FileMeta: {
              text: '{{ docsFile4Meta | polyglot }}',
              fontSize: 'Y',
              color: 'textMuted'
            }
          }
        },
        FileRight: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          FileDlBtn: {
            extends: 'Icon',
            icon: 'download',
            width: 'A',
            height: 'A',
            color: 'textMuted',
            cursor: 'pointer'
          }
        }
      },
      FileRow5: {
        flow: 'x',
        align: 'center space-between',
        padding: 'Z A',
        background: 'surfaceCard',
        border: '1px solid surfaceBorder',
        borderRadius: 'Z',
        FileLeft: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          FileIcon: { text: '📄', fontSize: 'A' },
          FileInfo: {
            flow: 'y',
            FileName: {
              text: '{{ docsFile5Name | polyglot }}',
              fontSize: 'Z',
              fontWeight: '500',
              color: 'text'
            },
            FileMeta: {
              text: '{{ docsFile5Meta | polyglot }}',
              fontSize: 'Y',
              color: 'textMuted'
            }
          }
        },
        FileRight: {
          flow: 'x',
          gap: 'Z',
          align: 'center',
          FileDlBtn: {
            extends: 'Icon',
            icon: 'download',
            width: 'A',
            height: 'A',
            color: 'textMuted',
            cursor: 'pointer'
          }
        }
      }
    }
  }
}
